import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import ItemPreviewDialog from "../ItemPreviewDialog";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

const DEFAULT_PAIR_COUNT = 2;

const resolveItemFromResponse = (response) => {
  if (!response) {
    return null;
  }

  if (response.item) {
    return response.item;
  }

  if (Array.isArray(response.items)) {
    return response.items[0] || null;
  }

  return response;
};

const extractItemIdentifier = (data) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  return data.id ?? data.Id ?? data.itemId ?? data.ItemId ?? undefined;
};

const cloneJsonValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneJsonValue(entry));
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = cloneJsonValue(value[key]);
      return acc;
    }, {});
  }

  return value;
};

const parseJsonTemplate = (item) => {
  if (!item || typeof item !== "object") {
    return { template: null, keys: [], serializeMap: {} };
  }

  const keys = [];
  const serializeMap = {};
  let selectedTemplate = null;

  const parseCandidate = (candidate) => {
    if (typeof candidate === "string") {
      try {
        return { template: JSON.parse(candidate), serialize: true };
      } catch {
        return { template: null, serialize: true };
      }
    }

    if (candidate && typeof candidate === "object") {
      return { template: candidate, serialize: false };
    }

    return { template: null, serialize: false };
  };

  ["json", "Json"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      const { template, serialize } = parseCandidate(item[key]);
      keys.push(key);
      serializeMap[key] = serialize;

      if (!selectedTemplate && template) {
        selectedTemplate = template;
      }
    }
  });

  return { template: selectedTemplate, keys, serializeMap };
};

const normalisePointsValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : value;
};

const createDefaultJsonTemplate = () => ({
  Type: "Match",
  Title: "",
  Options: [],
  Distractors: [],
});

const createEmptyItem = () => ({
  Type: "Match",
  Name: "",
  Title: "",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: Array.from({ length: DEFAULT_PAIR_COUNT }, (_, index) => ({
    id: index + 1,
    Name: "",
    Value: "",
    Rationale: "",
  })),
  Distractors: [],
});

const normalisePairsList = (pairs) => {
  if (!Array.isArray(pairs)) {
    return [];
  }

  return pairs.map((pair, index) => ({
    id: pair?.id ?? index + 1,
    Name: pair?.Name ?? pair?.name ?? "",
    Value: pair?.Value ?? pair?.value ?? "",
    Rationale: pair?.Rationale ?? pair?.rationale ?? "",
  }));
};

const normaliseDistractorsList = (distractors) => {
  if (!Array.isArray(distractors)) {
    return [];
  }

  return distractors.map((entry, index) => ({
    id: entry?.id ?? entry?.Id ?? index + 1,
    Value: entry?.Value ?? entry?.value ?? "",
    Rationale: entry?.Rationale ?? entry?.rationale ?? "",
  }));
};

const applyItemToJsonTemplate = (template, itemState, pairs, distractors, points) => {
  const base =
    template && typeof template === "object" ? cloneJsonValue(template) : null;

  const titleValue =
    itemState?.Title !== undefined && itemState?.Title !== null
      ? itemState.Title
      : "";
  const normalizedSkilltype = normalizeSkillTypeValue(itemState?.skilltype);

  if (base && typeof base === "object" && !Array.isArray(base)) {
    let handledPairs = false;

    if (
      Object.prototype.hasOwnProperty.call(base, "Options") ||
      Array.isArray(base.Options)
    ) {
      base.Options = pairs;
      handledPairs = true;
    }

    if (
      base.Question &&
      typeof base.Question === "object" &&
      !Array.isArray(base.Question)
    ) {
      const questionClone = cloneJsonValue(base.Question);

      if (
        Object.prototype.hasOwnProperty.call(questionClone, "Options") ||
        Array.isArray(questionClone.Options)
      ) {
        questionClone.Options = pairs;
        handledPairs = true;
      }

      if (titleValue !== undefined) {
        if (Object.prototype.hasOwnProperty.call(questionClone, "Title")) {
          questionClone.Title = titleValue;
        } else if (
          Object.prototype.hasOwnProperty.call(questionClone, "Prompt")
        ) {
          questionClone.Prompt = titleValue;
        } else if (
          Object.prototype.hasOwnProperty.call(questionClone, "Stem")
        ) {
          questionClone.Stem = titleValue;
        }
      }

      base.Question = questionClone;
    }

    if (!handledPairs) {
      base.Options = pairs;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "Distractors") ||
      Array.isArray(base.Distractors)
    ) {
      base.Distractors = distractors;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "Title") ||
      !handledPairs
    ) {
      base.Title = titleValue;
    }

    if (points !== undefined) {
      base.points = points;
    } else if (Object.prototype.hasOwnProperty.call(base, "points")) {
      delete base.points;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "Name") ||
      itemState?.Name
    ) {
      base.Name = itemState?.Name || "";
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "Type") ||
      itemState?.Type
    ) {
      base.Type = itemState?.Type || base.Type || "Match";
    }

    if (Object.prototype.hasOwnProperty.call(base, "skilltype")) {
      const effectiveSkilltype =
        normalizedSkilltype || normalizeSkillTypeValue(base.skilltype);

      base.skilltype = effectiveSkilltype;
    } else if (normalizedSkilltype) {
      base.skilltype = normalizedSkilltype;
    }

    if (
      Object.prototype.hasOwnProperty.call(base, "itemcat") ||
      itemState?.itemcat
    ) {
      base.itemcat = itemState?.itemcat || "";
    }

    return base;
  }

  const fallback = {
    Title: titleValue,
    Options: pairs,
  };

  if (points !== undefined) {
    fallback.points = points;
  }

  if (distractors?.length) {
    fallback.Distractors = distractors;
  }

  if (itemState?.Name) {
    fallback.Name = itemState.Name;
  }

  fallback.Type = itemState?.Type || "Match";

  if (normalizedSkilltype) {
    fallback.skilltype = normalizedSkilltype;
  }

  if (itemState?.itemcat) {
    fallback.itemcat = itemState.itemcat;
  }

  return fallback;
};

const MatchItemDetail = () => {
  const { itemId } = useParams();
  const isRouteNewItem = !itemId || itemId === "new";
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [nextPairId, setNextPairId] = useState(1);
  const [nextDistractorId, setNextDistractorId] = useState(1);
  const [effectiveItemId, setEffectiveItemId] = useState(
    isRouteNewItem ? null : itemId
  );
  const [jsonTemplate, setJsonTemplate] = useState(createDefaultJsonTemplate());
  const [jsonKeys, setJsonKeys] = useState(["Json"]);
  const [jsonSerializeMap, setJsonSerializeMap] = useState({ Json: false });
  const [lastSaveMode, setLastSaveMode] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (isRouteNewItem) {
      setEffectiveItemId(null);
    } else {
      setEffectiveItemId(itemId || null);
    }
  }, [isRouteNewItem, itemId]);

  useEffect(() => {
    if (!ItemService.isConfigured()) {
      setError("Items API URL is not configured.");

      if (isRouteNewItem) {
        const emptyItem = createEmptyItem();
        setItem(emptyItem);
        setJsonTemplate(createDefaultJsonTemplate());
        setJsonKeys(["Json"]);
        setJsonSerializeMap({ Json: false });
        setNextPairId(emptyItem.Options.length + 1);
        setNextDistractorId(1);
      } else {
        setItem(null);
      }

      return;
    }

    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setSaveError(null);
      setSaveSuccess(false);

      try {
        const [itemResponse, taxonomyResponse] = await Promise.all([
          isRouteNewItem ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
          ItemService.fetchTaxonomy("arabic"),
        ]);

        if (!isMounted) {
          return;
        }

        const resolvedItem = isRouteNewItem
          ? createEmptyItem()
          : resolveItemFromResponse(itemResponse);

        if (!resolvedItem) {
          throw new Error("Unable to locate the requested item.");
        }

        const resolvedPairs = normalisePairsList(resolvedItem.Options);
        const pairs = resolvedPairs.length
          ? resolvedPairs
          : createEmptyItem().Options;
        const resolvedDistractors = normaliseDistractorsList(
          resolvedItem.Distractors
        );

        const highestPairId = pairs.reduce((max, pair, index) => {
          const candidate = Number(pair?.id ?? index + 1) || 0;
          return candidate > max ? candidate : max;
        }, 0);

        const highestDistractorId = resolvedDistractors.reduce(
          (max, distractor, index) => {
            const candidate = Number(distractor?.id ?? index + 1) || 0;
            return candidate > max ? candidate : max;
          },
          0
        );

        setNextPairId(highestPairId > 0 ? highestPairId + 1 : pairs.length + 1);
        setNextDistractorId(
          highestDistractorId > 0
            ? highestDistractorId + 1
            : resolvedDistractors.length + 1
        );

        setItem({
          ...resolvedItem,
          Type: resolvedItem.Type || "Match",
          Options: pairs,
          Distractors: resolvedDistractors,
          skilltype: normalizeSkillTypeValue(resolvedItem.skilltype),
        });

        const jsonState = parseJsonTemplate(resolvedItem);

        if (jsonState.keys.length) {
          setJsonTemplate(jsonState.template || createDefaultJsonTemplate());
          setJsonKeys(jsonState.keys);
          setJsonSerializeMap(jsonState.serializeMap);
        } else {
          setJsonTemplate(createDefaultJsonTemplate());
          setJsonKeys(["Json"]);
          setJsonSerializeMap({ Json: false });
        }

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        const identifier = extractItemIdentifier(resolvedItem);
        setEffectiveItemId(
          isRouteNewItem ? null : identifier ?? itemId ?? null
        );
      } catch (err) {
        if (!isMounted) {
          return;
        }

        if (isRouteNewItem) {
          const emptyItem = createEmptyItem();
          setItem(emptyItem);
          setJsonTemplate(createDefaultJsonTemplate());
          setJsonKeys(["Json"]);
          setJsonSerializeMap({ Json: false });
          setNextPairId(emptyItem.Options.length + 1);
          setNextDistractorId(1);
          setTaxonomyOptions([]);
        } else {
          setItem(null);
        }

        setError(
          err?.response?.data?.message || err?.message || "Failed to load item."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [itemId, isRouteNewItem]);

  const handleFieldChange = useCallback((field, value) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const nextValue =
        field === "skilltype"
          ? normalizeSkillTypeValue(value)
          : value;

      return { ...prev, [field]: nextValue };
    });
  }, []);

  const handlePairChange = useCallback((index, field, value) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const pairs = Array.isArray(prev.Options) ? [...prev.Options] : [];

      if (!pairs[index]) {
        return prev;
      }

      pairs[index] = { ...pairs[index], [field]: value };

      return {
        ...prev,
        Options: pairs,
      };
    });
  }, []);

  const handleRemovePair = useCallback((index) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const pairs = Array.isArray(prev.Options) ? [...prev.Options] : [];
      pairs.splice(index, 1);

      return {
        ...prev,
        Options: pairs,
      };
    });
  }, []);

  const handleAddPair = useCallback(() => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const pairs = Array.isArray(prev.Options) ? [...prev.Options] : [];

      const newPair = {
        id: nextPairId,
        Name: "",
        Value: "",
        Rationale: "",
      };

      setNextPairId((current) => current + 1);

      return {
        ...prev,
        Options: [...pairs, newPair],
      };
    });
  }, [nextPairId]);

  const handleDistractorChange = useCallback((index, field, value) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const distractors = Array.isArray(prev.Distractors)
        ? [...prev.Distractors]
        : [];

      if (!distractors[index]) {
        return prev;
      }

      distractors[index] = { ...distractors[index], [field]: value };

      return {
        ...prev,
        Distractors: distractors,
      };
    });
  }, []);

  const handleRemoveDistractor = useCallback((index) => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const distractors = Array.isArray(prev.Distractors)
        ? [...prev.Distractors]
        : [];
      distractors.splice(index, 1);

      return {
        ...prev,
        Distractors: distractors,
      };
    });
  }, []);

  const handleAddDistractor = useCallback(() => {
    setItem((prev) => {
      if (!prev) {
        return prev;
      }

      const distractors = Array.isArray(prev.Distractors)
        ? [...prev.Distractors]
        : [];

      const newDistractor = {
        id: nextDistractorId,
        Value: "",
        Rationale: "",
      };

      setNextDistractorId((current) => current + 1);

      return {
        ...prev,
        Distractors: [...distractors, newDistractor],
      };
    });
  }, [nextDistractorId]);

  const { isItemcatValid, itemcatWarningMessage } = useMemo(() => {
    if (loading) {
      return { isItemcatValid: false, itemcatWarningMessage: null };
    }

    if (!taxonomyOptions.length) {
      return {
        isItemcatValid: false,
        itemcatWarningMessage:
          "No taxonomy results are available. Please refresh the page to try again.",
      };
    }

    if (!item?.itemcat) {
      return { isItemcatValid: false, itemcatWarningMessage: null };
    }

    const matchesTaxonomy = taxonomyOptions.some(
      (option) => option.value === item.itemcat
    );

    if (!matchesTaxonomy) {
      return {
        isItemcatValid: false,
        itemcatWarningMessage: `The selected item category "${item.itemcat}" is not part of the taxonomy results.`,
      };
    }

    return { isItemcatValid: true, itemcatWarningMessage: null };
  }, [item?.itemcat, loading, taxonomyOptions]);

  const isNameValid = useMemo(() => {
    if (!item?.Name || typeof item.Name !== "string") {
      return false;
    }

    return item.Name.trim().length > 0;
  }, [item?.Name]);

  const isSkillTypeValid = useMemo(() => {
    if (!item?.skilltype || typeof item.skilltype !== "string") {
      return false;
    }

    return item.skilltype.trim().length > 0;
  }, [item?.skilltype]);

  const isPointsValid = useMemo(() => {
    if (item?.points === null || item?.points === undefined || item?.points === "") {
      return false;
    }

    const numericValue = Number(item.points);
    return Number.isFinite(numericValue);
  }, [item?.points]);

  const hasPromptContent = useMemo(() => {
    if (!item?.Title || typeof item.Title !== "string") {
      return false;
    }

    const textContent = item.Title.replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();

    return textContent.length > 0;
  }, [item?.Title]);

  const hasMinimumValidPairs = useMemo(() => {
    if (!Array.isArray(item?.Options)) {
      return false;
    }

    const validPairs = item.Options.filter((pair) => {
      if (!pair) {
        return false;
      }

      const name = typeof pair.Name === "string" ? pair.Name.trim() : "";
      const value = typeof pair.Value === "string" ? pair.Value.trim() : "";

      return name.length > 0 && value.length > 0;
    });

    return validPairs.length >= DEFAULT_PAIR_COUNT;
  }, [item?.Options]);

  const isCreateMode = !effectiveItemId;

  const isCreateFormValid = useMemo(() => {
    if (!isCreateMode) {
      return true;
    }

    return (
      isNameValid &&
      isSkillTypeValid &&
      isItemcatValid &&
      isPointsValid &&
      hasPromptContent &&
      hasMinimumValidPairs
    );
  }, [
    hasMinimumValidPairs,
    hasPromptContent,
    isCreateMode,
    isItemcatValid,
    isNameValid,
    isPointsValid,
    isSkillTypeValid,
  ]);

  const handleSave = useCallback(async () => {
    if (!item) {
      return;
    }

    if (!isItemcatValid) {
      setSaveSuccess(false);
      return;
    }

    if (!isCreateFormValid) {
      setSaveSuccess(false);
      setSaveError("Please complete all required fields before saving.");
      return;
    }

    const isCreateAction = !effectiveItemId;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setLastSaveMode(isCreateAction ? "create" : "update");

    try {
      const normalizedPairs = normalisePairsList(item.Options);
      const normalizedDistractors = normaliseDistractorsList(item.Distractors);
      const normalizedPoints = normalisePointsValue(item.points);
      const normalizedSkilltype = normalizeSkillTypeValue(item.skilltype);

      const payload = {
        ...item,
        Type: item.Type || "Match",
        Options: normalizedPairs,
        Distractors: normalizedDistractors,
        skilltype: normalizedSkilltype,
      };

      if (normalizedPoints !== undefined) {
        payload.points = normalizedPoints;
      } else {
        delete payload.points;
      }

      const updatedJson = applyItemToJsonTemplate(
        jsonTemplate,
        item,
        normalizedPairs,
        normalizedDistractors,
        normalizedPoints
      );

      if (updatedJson && typeof updatedJson === "object") {
        const keysToUpdate = jsonKeys.length ? jsonKeys : ["Json"];
        const serializeMapForKeys = jsonKeys.length
          ? jsonSerializeMap
          : { Json: false };

        keysToUpdate.forEach((key) => {
          const shouldSerialize = serializeMapForKeys[key] ?? false;
          payload[key] = shouldSerialize
            ? JSON.stringify(updatedJson)
            : updatedJson;
        });

        if (!jsonKeys.length) {
          setJsonKeys(keysToUpdate);
          setJsonSerializeMap(serializeMapForKeys);
        }

        if (!keysToUpdate.includes("Json")) {
          payload.Json = updatedJson;
        }

        if (!keysToUpdate.includes("json")) {
          payload.json = JSON.stringify(updatedJson);
        }
      }

      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      const resolvedSavedItem = resolveItemFromResponse(response);
      const nextItem =
        resolvedSavedItem && typeof resolvedSavedItem === "object"
          ? resolvedSavedItem
          : { ...item, ...payload };

      const normalizedSavedPairs = normalisePairsList(nextItem.Options);
      const normalizedSavedDistractors = normaliseDistractorsList(
        nextItem.Distractors
      );

      const nextHighestPairId = normalizedSavedPairs.reduce(
        (max, pair, index) => {
          const candidate = Number(pair?.id ?? index + 1) || 0;
          return candidate > max ? candidate : max;
        },
        0
      );

      const nextHighestDistractorId = normalizedSavedDistractors.reduce(
        (max, distractor, index) => {
          const candidate = Number(distractor?.id ?? index + 1) || 0;
          return candidate > max ? candidate : max;
        },
        0
      );

      setNextPairId(
        nextHighestPairId > 0
          ? nextHighestPairId + 1
          : (normalizedSavedPairs.length || 0) + 1
      );
      setNextDistractorId(
        nextHighestDistractorId > 0
          ? nextHighestDistractorId + 1
          : (normalizedSavedDistractors.length || 0) + 1
      );

      setItem({
        ...nextItem,
        Type: nextItem.Type || "Match",
        Options: normalizedSavedPairs,
        Distractors: normalizedSavedDistractors,
        skilltype: normalizeSkillTypeValue(
          nextItem.skilltype ?? normalizedSkilltype
        ),
      });

      const identifier = extractItemIdentifier(nextItem);
      setEffectiveItemId(identifier ?? effectiveItemId ?? null);

      const nextJsonState = parseJsonTemplate(nextItem);

      if (nextJsonState.keys.length) {
        setJsonTemplate(nextJsonState.template || createDefaultJsonTemplate());
        setJsonKeys(nextJsonState.keys);
        setJsonSerializeMap(nextJsonState.serializeMap);
      }

      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || err?.message || "Unable to save item."
      );
      setSaveSuccess(false);
    } finally {
      setSaving(false);
    }
  }, [
    effectiveItemId,
    isCreateFormValid,
    isItemcatValid,
    item,
    jsonKeys,
    jsonSerializeMap,
    jsonTemplate,
  ]);

  const taxonomySelectOptions = useMemo(() => {
    const hasTaxonomyOptions = taxonomyOptions.length > 0;

    if (!hasTaxonomyOptions) {
      if (item?.itemcat) {
        return [{ value: item.itemcat, label: item.itemcat }];
      }

      return [];
    }

    if (!item?.itemcat) {
      return taxonomyOptions;
    }

    const matchesExistingOption = taxonomyOptions.some(
      (option) => option.value === item.itemcat
    );

    if (matchesExistingOption) {
      return taxonomyOptions;
    }

    return [
      { value: item.itemcat, label: item.itemcat },
      ...taxonomyOptions,
    ];
  }, [item?.itemcat, taxonomyOptions]);

  const skillSelectOptions = useMemo(() => {
    if (!item?.skilltype) {
      return BLOOMS_TAXONOMY_SKILLS;
    }

    const matchesBloomSkill = BLOOMS_TAXONOMY_SKILLS.some(
      (option) => option.value === item.skilltype
    );

    if (matchesBloomSkill) {
      return BLOOMS_TAXONOMY_SKILLS;
    }

    return [
      ...BLOOMS_TAXONOMY_SKILLS,
      { value: item.skilltype, label: item.skilltype },
    ];
  }, [item?.skilltype]);

  const actionButtonLabel = saving
    ? isCreateMode
      ? "Creating..."
      : "Saving..."
    : isCreateMode
    ? "Create"
    : "Save changes";

  const successMessage =
    lastSaveMode === "create"
      ? "Item created successfully."
      : "Item updated successfully.";

  const jsonPreviewData = useMemo(() => {
    if (!item) {
      return null;
    }

    const normalizedPairs = normalisePairsList(item.Options);
    const normalizedDistractors = normaliseDistractorsList(item.Distractors);
    const normalizedPoints = normalisePointsValue(item.points);

    const payload = {
      ...item,
      Type: item.Type || "Match",
      Options: normalizedPairs,
      Distractors: normalizedDistractors,
    };

    if (normalizedPoints !== undefined) {
      payload.points = normalizedPoints;
    } else {
      delete payload.points;
    }

    payload.skilltype = normalizeSkillTypeValue(item.skilltype);

    const updatedJson = applyItemToJsonTemplate(
      jsonTemplate,
      item,
      normalizedPairs,
      normalizedDistractors,
      normalizedPoints
    );

    if (updatedJson && typeof updatedJson === "object") {
      const keysToUpdate = jsonKeys.length ? jsonKeys : ["Json"];
      const serializeMapForKeys = jsonKeys.length
        ? jsonSerializeMap
        : { Json: false };

      keysToUpdate.forEach((key) => {
        const shouldSerialize = serializeMapForKeys[key] ?? false;
        payload[key] = shouldSerialize
          ? JSON.stringify(updatedJson)
          : updatedJson;
      });

      if (!keysToUpdate.includes("Json")) {
        payload.Json = updatedJson;
      }

      if (!keysToUpdate.includes("json")) {
        delete payload.json;
      }
    }

    if ("Json" in payload && "json" in payload) {
      delete payload.json;
    }

    return payload;
  }, [item, jsonKeys, jsonSerializeMap, jsonTemplate]);

  const jsonPreviewString = useMemo(
    () => (jsonPreviewData ? JSON.stringify(jsonPreviewData, null, 2) : ""),
    [jsonPreviewData]
  );

  const handleOpenJsonDialog = useCallback(() => {
    setJsonDialogOpen(true);
  }, []);

  const handleCloseJsonDialog = useCallback(() => {
    setJsonDialogOpen(false);
  }, []);

  const handleOpenPreview = useCallback(() => {
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const previewContent = useMemo(() => {
    if (!item) {
      return null;
    }

    return {
      title: item.Title || "",
      pairs: normalisePairsList(item.Options),
      distractors: normaliseDistractorsList(item.Distractors),
    };
  }, [item]);

  const canPreview = useMemo(() => {
    if (!item) {
      return false;
    }

    const hasTitle = Boolean(
      typeof item.Title === "string" &&
        item.Title.replace(/<[^>]*>/g, " ")
          .replace(/&nbsp;/g, " ")
          .trim()
    );

    const pairs = normalisePairsList(item.Options);
    const hasPair = pairs.some((pair) => {
      if (!pair) {
        return false;
      }

      const name = typeof pair.Name === "string" ? pair.Name.trim() : "";
      const value = typeof pair.Value === "string" ? pair.Value.trim() : "";

      return Boolean(name && value);
    });

    const distractors = normaliseDistractorsList(item.Distractors);
    const hasDistractor = distractors.some(
      (entry) => typeof entry?.Value === "string" && entry.Value.trim()
    );

    return hasTitle || hasPair || hasDistractor;
  }, [item]);

  if (loading) {
    return (
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <LinearProgress sx={{ width: "100%" }} />
          <Typography variant="body2" color="text.secondary">
            Loading item details…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Match item
          </Typography>
          <Alert severity="error">{error}</Alert>
        </Stack>
      </Paper>
    );
  }

  if (!item) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Paper
        elevation={1}
        sx={{
          "--Paper-shadow":
            "-3px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.32)",
          p: { xs: 2, md: 3 },
          boxShadow: "var(--Paper-shadow)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={item.Type || "Match"} color="secondary" variant="filled" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {item.Name || "Untitled Match item"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleOpenPreview}
                disabled={!canPreview}
                sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenJsonDialog}
                disabled={!jsonPreviewString}
                sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>
          {item.version && (
            <Typography variant="body2" color="text.secondary">
              Version {item.version}
            </Typography>
          )}
          <Divider />

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              {successMessage}
            </Alert>
          )}

          {saveError && <Alert severity="error">{saveError}</Alert>}

          {itemcatWarningMessage && (
            <Alert severity="warning">{itemcatWarningMessage}</Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item name"
                fullWidth
                required
                value={item.Name || ""}
                onChange={(event) => handleFieldChange("Name", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                label="Points"
                fullWidth
                required
                value={item.points ?? ""}
                onChange={(event) => {
                  const { value } = event.target;
                  handleFieldChange("points", value === "" ? "" : Number(value));
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel id="match-skilltype-label" required>
                  Skill type
                </InputLabel>
                <Select
                  labelId="match-skilltype-label"
                  label="Skill type"
                  value={item.skilltype || ""}
                  onChange={(event) =>
                    handleFieldChange("skilltype", event.target.value)
                  }
                >
                  <MenuItem value="" disabled>
                    <em>Not set</em>
                  </MenuItem>
                  {skillSelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(itemcatWarningMessage) && !loading}
              >
                <InputLabel id="match-itemcat-label" required>
                  Item category
                </InputLabel>
                <Select
                  labelId="match-itemcat-label"
                  label="Item category"
                  value={item.itemcat || ""}
                  onChange={(event) =>
                    handleFieldChange("itemcat", event.target.value)
                  }
                >
                  <MenuItem value="" disabled>
                    <em>Not set</em>
                  </MenuItem>
                  {taxonomySelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
              Prompt
            </Typography>
            <ReactQuill
              theme="snow"
              value={item.Title || ""}
              onChange={(value) => handleFieldChange("Title", value)}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Describe what learners should do..."
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Matching pairs
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Provide the term or prompt in the left column and the correct match in the right column. Optionally add a rationale learners will see after submission.
            </Typography>
          </Box>

          <Stack spacing={2}>
            {Array.isArray(item.Options) && item.Options.length > 0 ? (
              item.Options.map((pair, index) => (
                <Paper
                  key={pair.id || index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: (theme) =>
                      theme.palette.grey?.[500] ||
                      theme.palette.grey?.[600] ||
                      theme.palette.grey?.main ||
                      theme.palette.divider,
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <TextField
                        label={`Prompt ${index + 1}`}
                        fullWidth
                        value={pair.Name || ""}
                        onChange={(event) =>
                          handlePairChange(index, "Name", event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Correct match"
                        fullWidth
                        value={pair.Value || ""}
                        onChange={(event) =>
                          handlePairChange(index, "Value", event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Rationale"
                        fullWidth
                        multiline
                        minRows={2}
                        value={pair.Rationale || ""}
                        onChange={(event) =>
                          handlePairChange(index, "Rationale", event.target.value)
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <IconButton
                        color="error"
                        aria-label="Remove pair"
                        onClick={() => handleRemovePair(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            ) : (
              <Alert severity="info">No pairs available. Add one to begin.</Alert>
            )}
          </Stack>

          <Button startIcon={<AddIcon />} onClick={handleAddPair}>
            Add pair
          </Button>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Distractors
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Distractors appear in the answer list to challenge learners with close-but-incorrect options.
            </Typography>
          </Box>

          <Stack spacing={2}>
            {Array.isArray(item.Distractors) && item.Distractors.length > 0 ? (
              item.Distractors.map((distractor, index) => (
                <Paper
                  key={distractor.id || index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: (theme) =>
                      theme.palette.grey?.[400] ||
                      theme.palette.divider,
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={5}>
                      <TextField
                        label={`Distractor ${index + 1}`}
                        fullWidth
                        value={distractor.Value || ""}
                        onChange={(event) =>
                          handleDistractorChange(index, "Value", event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Rationale"
                        fullWidth
                        multiline
                        minRows={2}
                        value={distractor.Rationale || ""}
                        onChange={(event) =>
                          handleDistractorChange(
                            index,
                            "Rationale",
                            event.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <IconButton
                        color="error"
                        aria-label="Remove distractor"
                        onClick={() => handleRemoveDistractor(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            ) : (
              <Alert severity="info">
                No distractors yet. Add optional distractors to increase difficulty.
              </Alert>
            )}
          </Stack>

          <Button startIcon={<AddIcon />} onClick={handleAddDistractor}>
            Add distractor
          </Button>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent={{ sm: "space-between" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ pt: 1 }}
          >
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !isItemcatValid || !isCreateFormValid}
              sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
            >
              {actionButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={jsonDialogOpen}
        onClose={handleCloseJsonDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON preview</DialogTitle>
        <DialogContent dividers>
          {jsonPreviewString ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
              }}
            >
              {jsonPreviewString}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Unable to generate item JSON preview.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJsonDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <ItemPreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        type={item.Type || "Match"}
        name={item.Name || "Match item"}
        loading={false}
        error={null}
        content={previewContent}
        unavailableMessage={null}
      />
    </Stack>
  );
};

export default MatchItemDetail;
